"use client"

import { usePathname } from "next/navigation"
import { type ReactNode, useLayoutEffect, useRef } from "react"
import { isConsumerAppPath } from "../lib/consumer-navigation.ts"
import { resolveUserRoute } from "../lib/routes.ts"
import { AppShell } from "./app-shell.tsx"
import { ConsumerSessionProvider, useConsumerSessionMemory } from "./consumer-session.tsx"
import { ConsumerTabs } from "./consumer-tabs.tsx"

// Restore only the active view; previously visited tabs remain mounted but hidden.
const ConsumerScroll = ({ pathname }: { readonly pathname: string }) => {
  const memory = useConsumerSessionMemory()
  const epoch = memory?.epoch
  const lastPosition = useRef(0)
  const lastPath = useRef<string | null>(null)

  useLayoutEffect(() => {
    if (memory === null) return
    let leaving = false
    let restoring = true
    let frame = 0
    let settleFrame = 0
    const target = memory.scroll.get(pathname) ?? (lastPath.current === pathname ? window.scrollY : 0)
    lastPath.current = pathname
    const remember = () => {
      if (leaving || restoring || memory.epoch !== epoch) return
      lastPosition.current = window.scrollY
      memory.scroll.set(pathname, lastPosition.current)
      if (memory.scroll.size > 100) {
        const oldest = memory.scroll.keys().next().value
        if (oldest !== undefined) memory.scroll.delete(oldest)
      }
    }
    const beginNavigation = () => {
      remember()
      leaving = true
    }
    const click = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const link = event.target instanceof Element ? event.target.closest("a[href]") : null
      if (!(link instanceof HTMLAnchorElement) || link.target === "_blank" || link.hasAttribute("download")) return
      const url = new URL(link.href, window.location.href)
      if (url.origin === window.location.origin && url.pathname !== pathname) beginNavigation()
    }
    const historyReturn = () => {
      if (!leaving && !restoring && memory.epoch === epoch) memory.scroll.set(pathname, lastPosition.current)
      leaving = true
    }
    const observer = new MutationObserver(() => restore())
    const restore = () => {
      if (!restoring || leaving) return
      const content = Array.from(document.querySelectorAll<HTMLElement>("[data-app-content-path]")).find((element) => element.dataset["appContentPath"] === pathname && element.closest("[hidden]") === null)
      if (content === undefined) return
      observer.disconnect()
      const apply = () => window.scrollTo({ behavior: "instant", top: target })
      apply()
      // Next may apply its own scroll in a layout effect after the page mounts.
      frame = window.requestAnimationFrame(() => {
        apply()
        settleFrame = window.requestAnimationFrame(() => {
          apply()
          restoring = false
          remember()
        })
      })
    }
    observer.observe(document.getElementById("main-content") ?? document.body, { attributes: true, childList: true, subtree: true })
    restore()
    window.addEventListener("scroll", remember, { passive: true })
    window.addEventListener("cashhub:navigation-start", beginNavigation)
    window.addEventListener("popstate", historyReturn)
    document.addEventListener("click", click, true)
    return () => {
      observer.disconnect()
      window.cancelAnimationFrame(frame)
      window.cancelAnimationFrame(settleFrame)
      window.removeEventListener("scroll", remember)
      window.removeEventListener("cashhub:navigation-start", beginNavigation)
      window.removeEventListener("popstate", historyReturn)
      document.removeEventListener("click", click, true)
    }
  }, [epoch, memory, pathname])
  return null
}

export const ConsumerAppFrame = ({ children }: { readonly children: ReactNode }) => {
  const pathname = usePathname()
  const route = pathname === null ? null : resolveUserRoute(pathname)
  if (pathname === null || route === null || !isConsumerAppPath(pathname)) return children
  return (
    <ConsumerSessionProvider>
      <AppShell appSurface destination={route.destination} routeId={route.id} title={route.title}>
        <ConsumerScroll pathname={pathname} />
        <ConsumerTabs pathname={pathname}>{children}</ConsumerTabs>
      </AppShell>
    </ConsumerSessionProvider>
  )
}
